import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import * as React from 'react';
import ModalComponent from "../../misc/ModalComponent";
import { Button, UncontrolledTooltip } from "reactstrap";
import axios from "axios";
import StatusCode from "../../../util/StatusCode";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Loading from "../../misc/Loading";
import { useTranslation } from "react-i18next";
import L from 'leaflet';
import { GetGoogleDeviceIpAddress } from "../../../store/GoogleDevice/DeviceAction";
import { RequestState } from "../../../models/RequestState";
import { RootState } from "../../../store/store";
import { FlowSyncAccessPolicy, isAuthorized } from "../../../models/FlowSyncAccessPolicy";


interface DeviceViewMapProps {
    lastKnownNetwork: boolean,
    deviceId: string
}

const DeviceViewMap = (props: DeviceViewMapProps) => {
    const { t } = useTranslation(['device', 'common']);
    const dispatch = useDispatch();

    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const hasDeviceReadSensitive = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.GOOGLE_DEVICE_READ_SENSITIVE);

    const [open, setOpen] = useState<boolean>(false);
    const [statusCode, setStatusCode] = useState<StatusCode>(StatusCode.NONE);
    const [latLong, setLatLong] = useState<[number, number] | undefined>(undefined);
    const mapButtonWrapperRef = useRef<HTMLSpanElement>(null);

    const ipAddress = useSelector<RootState, RequestState<string>>(state => state.device.googleDeviceIpAddress)

    useEffect(() => {
        if (open && props.deviceId && hasDeviceReadSensitive) {
            GetGoogleDeviceIpAddress(props.deviceId)(dispatch);
        }
    }, [open, props.deviceId, dispatch, hasDeviceReadSensitive]);

    const icon = L.divIcon({
        html: `<svg style="width:100%; height:100%" data-prefix="fas" data-icon="location-dot" class="svg-inline--fa fa-location-dot icon" role="img" viewBox="0 0 384 512" aria-hidden="true"><path fill="blue" d="M0 188.6C0 84.4 86 0 192 0S384 84.4 384 188.6c0 119.3-120.2 262.3-170.4 316.8-11.8 12.8-31.5 12.8-43.3 0-50.2-54.5-170.4-197.5-170.4-316.8zM192 256a64 64 0 1 0 0-128 64 64 0 1 0 0 128z"></path></svg></svg>`,
        className: '',
        iconSize: [48, 48],
        iconAnchor: [24, 48],
    });

    useEffect(() => {
        if (!open) return;
        if (ipAddress.code !== StatusCode.COMPLETE) return;
        if (latLong) return;

        if (ipAddress.code === StatusCode.COMPLETE) {
            axios.get(`https://ipapi.co/${ipAddress.data}/latlong/`)
                .then(res => {
                    setStatusCode(StatusCode.COMPLETE);
                    const position: [number, number] = res.data.split(',')
                        .map(Number) as [number, number];
                    setLatLong(position);
                })
                .catch(error => {
                    setStatusCode(StatusCode.ERROR);
                })
        }
    }, [ipAddress]);

    const showMap = async () => {
        setOpen(true)
        setStatusCode(StatusCode.PENDING);
    }

    const renderMap = () => {
        if (!latLong) return;

        return (
            <MapContainer
                center={latLong}
                zoom={13}
                style={{ height: '500px', width: '100%' }}
            >
                <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <Marker position={latLong} icon={icon}>
                    <Popup>
                        {t('device.lastKnownNetwork.title', { ns: 'device' })}
                    </Popup>
                </Marker>
            </MapContainer>
        );
    }

    const renderBody = () => {
        if (statusCode === StatusCode.NONE)
            return;
        if (statusCode === StatusCode.PENDING)
            return <Loading />;
        if (statusCode === StatusCode.COMPLETE)
            return renderMap();
        if (statusCode === StatusCode.ERROR || ipAddress.code === StatusCode.ERROR)
            return t('error.somethingWentWrong', { ns: 'common' })
    }

    const toggleModal = () => {
        setOpen(false)
        setLatLong(undefined);
        setStatusCode(StatusCode.NONE);
    }

    if (open) {
        return (
            <ModalComponent
                isOpen={open}
                toggleModal={() => toggleModal()}
                header={t('googleDeviceListing.tableHeaders.lastKnownNetwork', { ns: 'device' })}
                size="xl"
                cancel={t('action.close', { ns: 'common' })}
            >
                {renderBody()}
            </ModalComponent>
        )
    }

    return (
        <>
            <span
                ref={mapButtonWrapperRef}
                style={{
                    display: 'inline-block',
                    cursor: !hasDeviceReadSensitive ? 'not-allowed' : 'default'
                }}
            >
                <Button
                    disabled={!hasDeviceReadSensitive}
                    onClick={() => showMap()}
                    style={!hasDeviceReadSensitive ? { pointerEvents: 'none' } : undefined}
                >
                    {t('device.lastKnownNetwork.show', { ns: 'device' })}
                </Button>
            </span>
            {!hasDeviceReadSensitive && (
                <UncontrolledTooltip target={mapButtonWrapperRef} autohide={false} placement="bottom">
                    {t('device.lastKnownNetwork.permissionDenied', { ns: 'device' })}
                </UncontrolledTooltip>
            )}
        </>
    )
}

export default DeviceViewMap;
